/**
 * SendChooser
 * The single "Send" affordance on a lead or client page: one button that opens a
 * short menu of the things that can actually be sent to this record — a business
 * card, or any of the messages that have been set up.
 *
 * Four things here are deliberate:
 *
 * 1. **The menu names the real messages, not a category.** The button it replaced
 *    said "Send Card", which was the only thing it could do, so the two
 *    reference-copy messages were unreachable from the record they are about —
 *    the only way in was a "Send a message" link in the Scheduled Messages panel
 *    further down the page, which reads as being about scheduling. Listing the
 *    templates by name means the answer to "how do I e-mail them the reference
 *    copy" is visible without opening anything.
 * 2. **Sending a card is still its own item.** A card e-mail is a fixed layout of
 *    a person's details, not a template with words, and it is what writes the
 *    lead note. Folding it into the message list would have meant deleting it.
 * 3. **The list loads when the menu is first opened**, not on page load. Nobody
 *    should pay for a request on every visit to a lead for a menu most visits
 *    never open.
 * 4. **A row is just a name; the description goes in one shared line at the
 *    foot of the menu.** Every message carries a sentence or two explaining what
 *    it is, and printing all of them at once turned a five-item menu into a
 *    paragraph nobody reads — the names, which are the thing you are choosing
 *    between, were buried in it. Now the list is five short lines you can scan,
 *    and whichever row the pointer or the keyboard is on explains itself
 *    underneath.
 *
 *    Two alternatives were rejected. *Expanding the hovered row in place* moves
 *    every row below it while the pointer is over the list, so the item you were
 *    aiming at slides away. *A "?" button inside each row* puts a second control
 *    inside a menu item, which is both a fiddly target and not something a menu
 *    can contain.
 *
 *    Nothing is hover-only. Keyboard focus fills the same line, every row points
 *    `aria-describedby` at its own description so a screen reader hears it with
 *    the item's name, and on touch — where there is no hover at all — the send
 *    dialog itself prints the chosen message's description under the Message
 *    box, where the choice can still be changed.
 *
 * This component only *chooses*. The dialogs stay owned by the page, which
 * already knows what to refresh after a send, and which also opens the message
 * dialog from the Scheduled Messages panel — one mounted dialog, two ways in.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { listComposableTemplates } from '../services/crm'
import { ChevronDownIcon } from './navIcons'
import type { ComposableTemplate } from '../types'

interface Props {
  /** Open the business-card dialog. */
  onChooseCard: () => void
  /** Open the message dialog with this template already selected. */
  onChooseTemplate: (templateId: number) => void
}

export default function SendChooser({ onChooseCard, onChooseTemplate }: Props) {
  const [open, setOpen] = useState(false)
  const [templates, setTemplates] = useState<ComposableTemplate[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  /** Description of the row the pointer or the keyboard is currently on. */
  const [detail, setDetail] = useState<string | null>(null)

  const wrapRef = useRef<HTMLDivElement | null>(null)
  const btnRef = useRef<HTMLButtonElement | null>(null)

  // Close when the click lands anywhere else, the same way the main navigation
  // dropdowns behave.
  useEffect(() => {
    if (!open) return
    function handleClickOutside(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // Escape closes and puts focus back on the button that opened it.
  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
        btnRef.current?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])

  async function handleToggle() {
    const next = !open
    setOpen(next)
    if (!next) setDetail(null)
    // Fetched once and kept. A failure leaves the card item working — the two
    // are independent, and losing the message list should not cost you the card.
    if (next && templates === null && !loading) {
      setLoading(true)
      setError('')
      try {
        setTemplates(await listComposableTemplates())
      } catch (err) {
        setTemplates([])
        setError(err instanceof Error ? err.message : 'Could not load the messages')
      } finally {
        setLoading(false)
      }
    }
  }

  function choose(action: () => void) {
    setOpen(false)
    setDetail(null)
    action()
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="send-chooser-menu"
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50"
      >
        <span>Send</span>
        <ChevronDownIcon
          className={`h-3 w-3 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          id="send-chooser-menu"
          role="menu"
          aria-label="Send menu"
          className="absolute right-0 mt-1 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50"
          // Clearing on the way out of the whole menu, rather than on the way
          // out of each row, is what keeps the description steady while the
          // pointer travels down the list and over the description itself.
          onMouseLeave={() => setDetail(null)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDetail(null)
          }}
        >
          <div className="py-1 max-h-80 overflow-y-auto">
            <MenuHeading>Business card</MenuHeading>
            <MenuItem
              label="Send a business card"
              description="Your details, laid out as a card."
              onDescribe={setDetail}
              onSelect={() => choose(onChooseCard)}
            />

            <div className="border-t border-gray-100 my-1" />
            <MenuHeading>Messages</MenuHeading>

            {loading && (
              <p className="px-3 py-2 text-sm text-gray-500">Loading&hellip;</p>
            )}
            {!loading && error && (
              <p className="px-3 py-2 text-sm text-red-600" role="alert">{error}</p>
            )}
            {!loading && !error && templates?.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-500">
                No messages have been set up yet.
              </p>
            )}
            {!loading &&
              templates?.map((t) => (
                <MenuItem
                  key={t.id}
                  label={t.name}
                  description={t.description ?? undefined}
                  onDescribe={setDetail}
                  onSelect={() => choose(() => onChooseTemplate(t.id))}
                />
              ))}
          </div>

          {/* One shared description line. `aria-hidden` because it is a visual
              echo of text each row already carries as its own accessible
              description — announcing it twice would be worse than not at all.
              `min-h` sized to the longest of the seeded descriptions so moving
              between rows never resizes the menu; it only appears once, when
              the first row is pointed at. */}
          {detail && (
            <p
              aria-hidden="true"
              className="border-t border-gray-100 px-3 py-2 text-xs leading-relaxed text-gray-500 min-h-[3.75rem] line-clamp-3"
            >
              {detail}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function MenuHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 pt-1 pb-0.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {children}
    </p>
  )
}

/**
 * One row. The description is rendered next to it in a `hidden` span rather
 * than not at all: a description reference is followed into hidden content, so
 * a screen reader hears the explanation with the item's name while the sighted
 * list stays one line per row.
 */
function MenuItem({
  label,
  description,
  onDescribe,
  onSelect,
}: {
  label: string
  description?: string
  onDescribe: (description: string | null) => void
  onSelect: () => void
}) {
  const descId = useId()
  return (
    <>
      <button
        type="button"
        role="menuitem"
        onClick={onSelect}
        onMouseEnter={() => onDescribe(description ?? null)}
        onFocus={() => onDescribe(description ?? null)}
        aria-describedby={description ? descId : undefined}
        className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
      >
        {label}
      </button>
      {description && (
        <span id={descId} hidden>
          {description}
        </span>
      )}
    </>
  )
}
